import React, { useState, useEffect } from 'react';
import { connect } from 'react-redux';
import { 
  Container, 
  Row, 
  Col, 
  Card, 
  Form, 
  Button, 
  Alert, 
  Spinner,
  InputGroup
} from 'react-bootstrap';
import { CreditCard, Key, Globe, Save, CheckCircle } from 'react-feather';
import SettingsService from '../../../services/SettingsService';

const StripeSettings = ({ user }) => {
  const [settings, setSettings] = useState({
    stripe_publishable_key: '',
    stripe_secret_key: '',
    stripe_webhook_secret: '',
    stripe_currency: 'usd'
  });
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    setLoading(true);
    try {
      const response = await SettingsService.getStripeSettings();
      console.log('Fetched Stripe settings:', response);
      
      if (response.success) {
        setSettings(prev => ({
          ...prev,
          ...response.settings
        }));
      } else {
        console.error('Failed to load settings:', response.error);
        setError('Failed to load Stripe settings: ' + (response.error || ''));
      }
    } catch (err) {
      console.error('Error loading Stripe settings:', err);
      setError('Failed to load Stripe settings: ' + (err.message || err.error || ''));
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setSettings(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError(null);
    setSuccess(null);

    console.log('Submitting Stripe settings:', {
      hasPublishableKey: !!settings.stripe_publishable_key,
      hasSecretKey: !!settings.stripe_secret_key,
      hasWebhookSecret: !!settings.stripe_webhook_secret,
      currency: settings.stripe_currency
    });

    try {
      const response = await SettingsService.updateStripeSettings(settings);
      console.log('Stripe settings response:', response);
      
      if (response.success) {
        setSuccess('Stripe settings saved successfully!');
        fetchSettings(); // Refresh to get masked values
      } else {
        console.error('Failed to save settings:', response.error);
        setError(response.error || 'Failed to save settings');
      }
    } catch (err) {
      console.error('Error saving Stripe settings:', err);
      setError(err.message || err.error || 'Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Container className="py-5 text-center">
        <Spinner animation="border" variant="primary" />
        <p className="mt-3">Loading settings...</p>
      </Container>
    );
  }

  return (
    <Container className="py-4">
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h2 className="mb-0">
          <CreditCard className="me-2" size={28} />
          Stripe Configuration
        </h2>
      </div>

      {error && (
        <Alert variant="danger" dismissible onClose={() => setError(null)} className="mb-4">
          {error}
        </Alert>
      )}

      {success && (
        <Alert variant="success" dismissible onClose={() => setSuccess(null)} className="mb-4">
          <CheckCircle size={18} className="me-2" />
          {success}
        </Alert>
      )}

      <Row>
        <Col lg={8}>
          <Card>
            <Card.Header>
              <h5 className="mb-0">API Keys</h5>
            </Card.Header>
            <Card.Body>
              <Form onSubmit={handleSubmit}>
                <Form.Group className="mb-3">
                  <Form.Label>
                    <Key size={16} className="me-2" />
                    Publishable Key
                  </Form.Label>
                  <Form.Control
                    type="text"
                    name="stripe_publishable_key"
                    value={settings.stripe_publishable_key}
                    onChange={handleChange}
                    placeholder="pk_test_..."
                  />
                  <Form.Text className="text-muted">
                    Starts with pk_test_ (test) or pk_live_ (production)
                  </Form.Text>
                </Form.Group>

                <Form.Group className="mb-3">
                  <Form.Label>
                    <Key size={16} className="me-2" />
                    Secret Key
                  </Form.Label>
                  <Form.Control
                    type="password"
                    name="stripe_secret_key"
                    value={settings.stripe_secret_key}
                    onChange={handleChange}
                    placeholder="sk_test_... or ••••••••"
                  />
                  <Form.Text className="text-muted">
                    Starts with sk_test_ (test) or sk_live_ (production). Leave as •••••••• to keep unchanged.
                  </Form.Text>
                </Form.Group>

                <Form.Group className="mb-3">
                  <Form.Label>
                    <Key size={16} className="me-2" />
                    Webhook Secret
                  </Form.Label>
                  <Form.Control
                    type="password"
                    name="stripe_webhook_secret"
                    value={settings.stripe_webhook_secret}
                    onChange={handleChange}
                    placeholder="whsec_... or ••••••••"
                  />
                  <Form.Text className="text-muted">
                    Starts with whsec_. Required for webhook events. Leave as •••••••• to keep unchanged.
                  </Form.Text>
                </Form.Group>

                <Form.Group className="mb-4">
                  <Form.Label>
                    <Globe size={16} className="me-2" />
                    Currency
                  </Form.Label>
                  <Form.Select
                    name="stripe_currency"
                    value={settings.stripe_currency}
                    onChange={handleChange}
                  >
                    <option value="usd">USD - US Dollar</option>
                    <option value="eur">EUR - Euro</option>
                    <option value="gbp">GBP - British Pound</option>
                    <option value="cad">CAD - Canadian Dollar</option>
                    <option value="aud">AUD - Australian Dollar</option>
                  </Form.Select>
                </Form.Group>

                <Button 
                  variant="primary" 
                  type="submit" 
                  disabled={saving}
                  className="d-flex align-items-center"
                >
                  {saving ? (
                    <>
                      <Spinner size="sm" className="me-2" />
                      Saving...
                    </>
                  ) : (
                    <>
                      <Save size={18} className="me-2" />
                      Save Settings
                    </>
                  )}
                </Button>
              </Form>
            </Card.Body>
          </Card>
        </Col>

        <Col lg={4}>
          <Card className="bg-light">
            <Card.Header>
              <h5 className="mb-0">Setup Guide</h5>
            </Card.Header>
            <Card.Body>
              <h6>1. Create Stripe Account</h6>
              <p className="small text-muted mb-3">
                Sign up at <a href="https://stripe.com" target="_blank" rel="noopener noreferrer">stripe.com</a>
              </p>

              <h6>2. Get API Keys</h6>
              <p className="small text-muted mb-3">
                Go to Developers → API Keys in your Stripe Dashboard
              </p>

              <h6>3. Configure Webhook</h6>
              <p className="small text-muted mb-3">
                Add endpoint: <code>{window.location.origin}/api/stripe/webhook</code>
              </p>

              <h6>4. Test Mode</h6>
              <p className="small text-muted mb-0">
                Use test keys (pk_test_, sk_test_) for development
              </p>
            </Card.Body>
          </Card>

          <Card className="mt-3 border-warning">
            <Card.Body>
              <h6 className="text-warning">⚠️ Important</h6>
              <p className="small text-muted mb-0">
                Never share your Secret Key or Webhook Secret. These should only be stored on your server.
              </p>
            </Card.Body>
          </Card>
        </Col>
      </Row>
    </Container>
  );
};

const mapStateToProps = (state) => ({
  user: state.auth.user
});

export default connect(mapStateToProps)(StripeSettings);
